/**
 * ContractWizard — Multi-step contract creation wizard.
 * 
 * Refactored: Step JSX extracted into components/wizard/*.jsx
 * This shell manages state, effects, and orchestrates the steps.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { WizardStepper } from './WizardStepper';
import { API_BASE as API } from '../utils/constants';
import {
  WizardStepChild,
  WizardStepContract,
  WizardStepRemuneration,
  WizardStepSchedule,
  WizardStepClauses,
} from './wizard';

const TOTAL_STEPS = 5;
const STEP_LABELS = [
  "Enfant",
  "Nature du contrat",
  "Rémunération",
  "Planning & Congés",
  "Clauses & Conditions",
];

const ContractWizard = ({ isOpen, onClose, onContractCreated }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  
  const _todayStr = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })();

  // ── Step state ──
  const [childData, setChildData] = useState({
    child_name: '', child_first_name: '', child_birth_date: '', child_sex: '',
  });
  
  const [contractData, setContractData] = useState({
    contract_type: 'cdi', year_type: 'complete', weeks_per_year: 52,
    start_date: _todayStr, end_date: '', signature_date: _todayStr,
    trial_period_weeks: 0, notice_period_months: 1, adaptation_details: '',
  });

  const [remunerationData, setRemunerationData] = useState({
    hourly_rate: '', payment_method: 'virement', overtime_rate: '',
    meal_price: '', snack_price: '', daily_allowance: '', km_allowance: '',
  });

  const [scheduleData, setScheduleData] = useState({
    working_days: [], daily_schedule: {}, weekly_rest_day: 'sunday',
    leave_mode: 'ouvrables', leave_payment_mode: 'maintien',
    work_on_holidays: false, planning_notice_days: 15, schedule_notes: '',
    postal_code: '', department: '', worked_holidays: [], holiday_surcharge_rate: 25,
  });

  const [clausesData, setClausesData] = useState({
    outing_conditions: 'Le salarié est autorisé à emmener l\'enfant dans ses déplacements quotidiens (courses, promenades, activités de loisirs).',
    authorized_persons: '', emergency_contacts: '', has_pets: false, pets_details: '',
    special_needs: '', periscolaire: false, periscolaire_details: '',
    employer_constraints: '', medical_authorization: true, transport_authorization: false,
  });

  // ── Holiday state ──
  const [availableHolidays, setAvailableHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(false);
  
  // ── Fetch holidays ──
  const fetchHolidays = useCallback(async (dept) => {
    if (!dept) { setAvailableHolidays([]); return; }
    setLoadingHolidays(true);
    try {
      const year = new Date().getFullYear();
      const res = await axios.get(`${API}/holidays/${year}?department=${dept}`);
      setAvailableHolidays(res.data.holidays || []);
    } catch (err) {
      console.warn('[ContractWizard] holidays fetch failed:', err.message || err);
      try {
        const year = new Date().getFullYear();
        const res = await axios.get(`${API}/holidays/${year}`);
        setAvailableHolidays(res.data.holidays || []);
      } catch (err2) {
        console.warn('[ContractWizard] holidays fallback also failed:', err2.message || err2);
        setAvailableHolidays([]);
      }
    } finally { setLoadingHolidays(false); }
  }, []);

  useEffect(() => {
    if (step === 4) fetchHolidays(scheduleData.department || '');
  }, [step, scheduleData.department, fetchHolidays]);

  // ── Schedule handlers ──
  const handlePostalCodeChange = (pc) => {
    setScheduleData(prev => ({ ...prev, postal_code: pc }));
    if (pc && pc.length >= 2) {
      let dept = pc.slice(0, 2);
      if (pc.startsWith('97') && pc.length >= 3) dept = pc.slice(0, 3);
      setScheduleData(prev => ({ ...prev, postal_code: pc, department: dept }));
    }
  };

  const toggleHolidayWorked = (dateStr) => {
    setScheduleData(prev => {
      const current = new Set(prev.worked_holidays);
      if (current.has(dateStr)) { current.delete(dateStr); } else { current.add(dateStr); }
      return { ...prev, worked_holidays: Array.from(current) };
    });
  };

  const toggleAllHolidays = (worked) => {
    if (worked) {
      const nonMay1 = availableHolidays.filter(h => !h.is_may_first).map(h => h.date);
      setScheduleData(prev => ({ ...prev, work_on_holidays: true, worked_holidays: nonMay1 }));
    } else {
      setScheduleData(prev => ({ ...prev, work_on_holidays: false, worked_holidays: [] }));
    }
  };

  const handleDayToggle = (dayId, checked) => {
    const newWorkingDays = checked 
      ? [...scheduleData.working_days, dayId]
      : scheduleData.working_days.filter(d => d !== dayId);
    const newSchedule = { ...scheduleData.daily_schedule };
    if (checked && !newSchedule[dayId]) { newSchedule[dayId] = { start: '08:00', end: '18:00' }; }
    else if (!checked) { delete newSchedule[dayId]; }
    setScheduleData({ ...scheduleData, working_days: newWorkingDays, daily_schedule: newSchedule });
  };
  
  const handleScheduleChange = (dayId, field, value) => {
    setScheduleData({
      ...scheduleData,
      daily_schedule: { ...scheduleData.daily_schedule, [dayId]: { ...scheduleData.daily_schedule[dayId], [field]: value } }
    });
  };

  const calculateWeeklyHours = () => {
    let total = 0;
    for (const [day, sched] of Object.entries(scheduleData.daily_schedule)) {
      if (scheduleData.working_days.includes(day) && sched.start && sched.end) {
        const [sh, sm] = sched.start.split(':').map(Number);
        const [eh, em] = sched.end.split(':').map(Number);
        const hours = (eh + em / 60) - (sh + sm / 60);
        if (hours > 0) total += hours;
      }
    }
    return Math.round(total * 100) / 100;
  };

  // ── Reset & Close ──
  const resetWizard = () => {
    setStep(1);
    setChildData({ child_name: '', child_first_name: '', child_birth_date: '', child_sex: '' });
    setContractData({ contract_type: 'cdi', year_type: 'complete', weeks_per_year: 52, start_date: _todayStr, end_date: '', signature_date: _todayStr, trial_period_weeks: 0, notice_period_months: 1, adaptation_details: '' });
    setRemunerationData({ hourly_rate: '', payment_method: 'virement', overtime_rate: '', meal_price: '', snack_price: '', daily_allowance: '', km_allowance: '' });
    setScheduleData({ working_days: [], daily_schedule: {}, weekly_rest_day: 'sunday', leave_mode: 'ouvrables', leave_payment_mode: 'maintien', work_on_holidays: false, planning_notice_days: 15, schedule_notes: '', postal_code: '', department: '', worked_holidays: [], holiday_surcharge_rate: 25 });
    setAvailableHolidays([]);
    setClausesData({ outing_conditions: 'Le salarié est autorisé à emmener l\'enfant dans ses déplacements quotidiens (courses, promenades, activités de loisirs).', authorized_persons: '', emergency_contacts: '', has_pets: false, pets_details: '', special_needs: '', periscolaire: false, periscolaire_details: '', employer_constraints: '', medical_authorization: true, transport_authorization: false });
  };
  const handleClose = () => { resetWizard(); onClose(); };

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const weeklyHours = calculateWeeklyHours();
      const payload = {
        child_name: childData.child_name, child_first_name: childData.child_first_name || undefined,
        child_birth_date: childData.child_birth_date || undefined, child_sex: childData.child_sex || undefined,
        contract_type: contractData.contract_type, year_type: contractData.year_type,
        weeks_per_year: contractData.year_type === 'incomplete' ? parseInt(contractData.weeks_per_year) : 52,
        hours_per_week: weeklyHours || undefined,
        start_date: contractData.start_date, end_date: contractData.end_date || undefined,
        signature_date: contractData.signature_date || undefined,
        trial_period_weeks: parseInt(contractData.trial_period_weeks) || 0,
        notice_period_months: parseInt(contractData.notice_period_months) || 1,
        adaptation_details: contractData.adaptation_details || undefined,
        hourly_rate: parseFloat(remunerationData.hourly_rate),
        payment_method: remunerationData.payment_method,
        overtime_rate: remunerationData.overtime_rate ? parseFloat(remunerationData.overtime_rate) : undefined,
        meal_price: remunerationData.meal_price ? parseFloat(remunerationData.meal_price) : 0,
        snack_price: remunerationData.snack_price ? parseFloat(remunerationData.snack_price) : 0,
        daily_allowance: remunerationData.daily_allowance ? parseFloat(remunerationData.daily_allowance) : 0,
        km_allowance: remunerationData.km_allowance ? parseFloat(remunerationData.km_allowance) : 0,
        working_days: scheduleData.working_days, daily_schedule: scheduleData.daily_schedule,
        weekly_rest_day: scheduleData.weekly_rest_day, leave_mode: scheduleData.leave_mode,
        leave_payment_mode: scheduleData.leave_payment_mode,
        work_on_holidays: scheduleData.worked_holidays.length > 0,
        planning_notice_days: parseInt(scheduleData.planning_notice_days) || 15,
        schedule_notes: scheduleData.schedule_notes || undefined,
        holiday_policy: {
          department: scheduleData.department || undefined,
          worked_holidays: scheduleData.worked_holidays,
          holiday_surcharge_rate: scheduleData.worked_holidays.length > 0 ? (parseFloat(scheduleData.holiday_surcharge_rate) || 0) / 100 : 0,
        },
        contract_year_type: scheduleData.contract_year_type || 'complete',
        monthly_salary: scheduleData.monthly_salary ? parseFloat(scheduleData.monthly_salary) : undefined,
        auto_approve_leave: scheduleData.auto_approve_leave || false,
        allow_anticipation_leave: scheduleData.allow_anticipation_leave || false,
        outing_conditions: clausesData.outing_conditions || undefined,
        authorized_persons: clausesData.authorized_persons || undefined,
        emergency_contacts: clausesData.emergency_contacts || undefined,
        has_pets: clausesData.has_pets, pets_details: clausesData.pets_details || undefined,
        special_needs: clausesData.special_needs || undefined,
        periscolaire: clausesData.periscolaire, periscolaire_details: clausesData.periscolaire_details || undefined,
        employer_constraints: clausesData.employer_constraints || undefined,
        medical_authorization: clausesData.medical_authorization,
        transport_authorization: clausesData.transport_authorization,
      };
      await axios.post(`${API}/contracts`, payload);
      toast.success(`Contrat créé pour ${childData.child_first_name || childData.child_name} ! Vous pouvez maintenant inviter une nounou.`);
      handleClose();
      onContractCreated();
    } catch (error) {
      console.error('Error creating contract:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally { setSubmitting(false); }
  };

  // ── Navigation buttons (shared by all steps) ──
  const NavigationButtons = ({ onBack, isLast = false }) => (
    <div className="flex justify-between gap-3 pt-4">
      {onBack ? (
        <Button type="button" variant="outline" onClick={onBack} data-testid={`wizard-step${step}-back`}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Retour
        </Button>
      ) : (
        <Button type="button" variant="outline" onClick={handleClose}>Annuler</Button>
      )}
      <Button 
        type="submit" disabled={isLast && submitting}
        style={{ backgroundColor: isLast ? 'var(--secondary-400)' : 'var(--primary-400)' }}
        data-testid={`wizard-step${step}-next`}
      >
        {isLast ? (submitting ? 'Création...' : 'Créer le contrat') : 'Suivant'}
        {!isLast && <ArrowRight className="ml-2 h-4 w-4" />}
      </Button>
    </div>
  );

  // ── Render ──
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl bg-[var(--bg-primary)] max-h-[90vh] overflow-y-auto" data-testid="contract-wizard" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <DialogHeader>
          <DialogTitle className="text-xl" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>
            Étape {step}/{TOTAL_STEPS} : {STEP_LABELS[step - 1]}
          </DialogTitle>
          <DialogDescription style={{ color: 'var(--text-secondary)' }}>
            {step === 1 && "Renseignez les informations de l'enfant accueilli"}
            {step === 2 && "Définissez le type de contrat, les dates et la période d'essai"}
            {step === 3 && "Fixez la rémunération et les indemnités"}
            {step === 4 && "Planifiez les horaires, congés et jours fériés"}
            {step === 5 && "Précisez les conditions d'accueil et clauses particulières"}
          </DialogDescription>
        </DialogHeader>
        
        {/* Progress Steps */}
        <WizardStepper
          totalSteps={TOTAL_STEPS}
          currentStep={step}
          labels={STEP_LABELS}
        />
        
        {/* ── Step Forms ── */}
        {step === 1 && (
          <form onSubmit={(e) => { e.preventDefault(); setStep(2); }}>
            <WizardStepChild data={childData} onChange={setChildData}>
              <NavigationButtons onBack={null} />
            </WizardStepChild>
          </form>
        )}
        
        {step === 2 && (
          <form onSubmit={(e) => { e.preventDefault(); setStep(3); }}>
            <WizardStepContract data={contractData} onChange={setContractData}>
              <NavigationButtons onBack={() => setStep(1)} />
            </WizardStepContract>
          </form>
        )}
        
        {step === 3 && (
          <form onSubmit={(e) => { e.preventDefault(); setStep(4); }}>
            <WizardStepRemuneration data={remunerationData} onChange={setRemunerationData}>
              <NavigationButtons onBack={() => setStep(2)} />
            </WizardStepRemuneration>
          </form>
        )}
        
        {step === 4 && (
          <form onSubmit={(e) => { e.preventDefault(); setStep(5); }}>
            <WizardStepSchedule
              data={scheduleData}
              onChange={setScheduleData}
              availableHolidays={availableHolidays}
              loadingHolidays={loadingHolidays}
              onDayToggle={handleDayToggle}
              onScheduleChange={handleScheduleChange}
              onPostalCodeChange={handlePostalCodeChange}
              onToggleHolidayWorked={toggleHolidayWorked}
              onToggleAllHolidays={toggleAllHolidays}
              weeklyHours={calculateWeeklyHours()}
            >
              <NavigationButtons onBack={() => setStep(3)} />
            </WizardStepSchedule>
          </form>
        )}
        
        {step === 5 && (
          <form onSubmit={handleSubmit}>
            <WizardStepClauses data={clausesData} onChange={setClausesData}>
              <NavigationButtons onBack={() => setStep(4)} isLast={true} />
            </WizardStepClauses>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContractWizard;
